import { type MouseEvent } from 'react';

type Color = 'secondary';

/**
 * Creates a ripple effect on an element.
 * Can be called with a MouseEvent (ripple originates from click position)
 * or with just an HTMLElement (ripple originates from center).
 */
// TypeScript function overloads - disable no-redeclare for overload signatures
// eslint-disable-next-line no-redeclare
function createRipple(event: MouseEvent<HTMLElement>, color?: Color): void;
// eslint-disable-next-line no-redeclare
function createRipple(element: HTMLElement, color?: Color): void;
// eslint-disable-next-line no-redeclare
function createRipple(
  eventOrElement: MouseEvent<HTMLElement> | HTMLElement,
  color?: Color,
): void {
  const isElement = eventOrElement instanceof HTMLElement;
  const currentTarget = isElement ? eventOrElement : eventOrElement.currentTarget;
  const clientX = isElement ? 0 : eventOrElement.clientX;
  const clientY = isElement ? 0 : eventOrElement.clientY;
  const borderWidth = Number.parseInt(
    getComputedStyle(currentTarget).getPropertyValue('border-width'),
    10,
  );
  const { left, top, width, height } = currentTarget.getBoundingClientRect();
  const x = clientX ? clientX - left : width / 2;
  const y = clientY ? clientY - top : height / 2;

  currentTarget.classList.add('ripple');

  const circle = document.createElement('span');
  const diameter = Math.max(currentTarget?.clientWidth, currentTarget?.clientHeight);
  const radius = diameter / 2;

  circle.style.width = `${diameter}px`;
  circle.style.height = `${diameter}px`;
  circle.style.left = `${x - borderWidth - radius}px`;
  circle.style.top = `${y - borderWidth - radius}px`;
  circle.classList.add('ripple__circle');
  if (color) {
    circle.classList.add(`ripple__circle--${color}`);
  }

  const ripple = currentTarget?.getElementsByClassName('ripple__circle')[0];

  if (ripple) {
    ripple.remove();
  }

  currentTarget?.appendChild(circle);
  circle.addEventListener('animationend', () => currentTarget?.removeChild(circle));
}

export default createRipple;
